import { useEffect, useState, useRef } from 'react'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting'
import { Box, Typography } from '@mui/material'
import LinearProgress from '@mui/material/LinearProgress'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import { loadStateFromLocalStorage } from '../../redux/utilsLocalStorage'
import moment from 'moment-timezone'
import { formatMinutesAsTime } from '../../utils/time'

const TIMESTAMP = 'Timestamp' // that should not change in future

// Series to skip entirely (right-side Y-axis series not needed for compare)
const SKIP_SERIES = new Set([
  'Uncertainty_flag',
  'Uncertainty_delta',
  'Uncertainty_flag_plus',
  'Uncertainty_flag_minus',
  'Icing_binary_50',
  'Icing_binary_physical_50',
  'Icing_power_loss_50',
  'Icing_probability_50',
  'Windspeed_50',
  'Windspeed_online',
])

// Only these series are visible at the start, all others are hidden
const SHOW_ON_START = new Set([
  'Forecast P50',
  'Online',
  'Forecast P50 (S1)',
  'Forecast P50 (S2)',
  'Online (S1)',
  'Online (S2)',
])

// labels for legend mapping
const legendLabels = {
  Forecast_10: 'Forecast P10',
  Forecast_25: 'Forecast P25',
  Forecast_50: 'Forecast P50',
  Forecast_75: 'Forecast P75',
  Forecast_90: 'Forecast P90',
  Combined_10: 'Combined P10',
  Combined_25: 'Combined P25',
  Combined_50: 'Combined P50',
  Combined_75: 'Combined P75',
  Combined_90: 'Combined P90',
  Ecmwf_10: 'ECMWF P10',
  Ecmwf_25: 'ECMWF P25',
  Ecmwf_50: 'ECMWF P50',
  Ecmwf_75: 'ECMWF P75',
  Ecmwf_90: 'ECMWF P90',
  Etaec_10: 'ETAEC P10',
  Etaec_25: 'ETAEC P25',
  Etaec_50: 'ETAEC P50',
  Etaec_75: 'ETAEC P75',
  Etaec_90: 'ETAEC P90',
  Gfs_eta_10: 'GFS ETA P10',
  Gfs_eta_25: 'GFS ETA P25',
  Gfs_eta_50: 'GFS ETA P50',
  Gfs_eta_75: 'GFS ETA P75',
  Gfs_eta_90: 'GFS ETA P90',
  Icon_10: 'ICON P10',
  Icon_20: 'ICON P20',
  Icon_50: 'ICON P50',
  Icon_75: 'ICON P75',
  Icon_90: 'ICON P90',
  Ukmo_10: 'UKMO P10',
  Ukmo_25: 'UKMO P25',
  Ukmo_50: 'UKMO P50',
  Ukmo_75: 'UKMO P75',
  Ukmo_90: 'UKMO P90',
  Ukmo100_10: 'UKMO100 P10',
  Ukmo100_20: 'UKMO100 P20',
  Ukmo100_50: 'UKMO100 P50',
  Ukmo100_75: 'UKMO100 P75',
  Ukmo100_90: 'UKMO100 P90',
}

// Build S1/S2 legend labels dynamically from the base labels
const buildCompareLegendLabels = () => {
  const labels = {}
  Object.entries(legendLabels).forEach(([key, displayName]) => {
    labels[key + '_S1'] = displayName + ' (S1)'
    labels[key + '_S2'] = displayName + ' (S2)'
  })
  const commonFields = [
    'Online',
    'Metering',
    'Availability',
    'Capacity',
    'Curtailment',
    'Downregulation',
  ]
  commonFields.forEach(field => {
    labels[field + '_S1'] = field + ' (S1)'
    labels[field + '_S2'] = field + ' (S2)'
  })
  return labels
}

const compareLegendLabels = buildCompareLegendLabels()

const useStyles = makeStyles(theme => ({
  customSubtitle: {
    fontSize: 16,
    color: theme.palette.primary.main,
    textAlign: 'center',
    marginBottom: '30px',
  },
}))

const unitsConversion = (clientUnit, clientResolution, data) => {
  const ratio = 60 / clientResolution

  const convert = (item, multiplier, applyRatio, noRatio = []) => {
    const newItem = { ...item }
    Object.keys(newItem).forEach(key => {
      if (key === TIMESTAMP) return
      if (newItem[key] === null) return
      if (newItem[key] === undefined) return
      if (newItem[key] === 0) return

      const useRatio = applyRatio && !noRatio.includes(key)

      newItem[key] = useRatio
        ? (newItem[key] / ratio) * multiplier
        : newItem[key] * multiplier
    })
    return newItem
  }

  if (clientUnit === 'kWh') {
    return data.map(item =>
      convert(item, 1, true, ['Availability', 'Capacity']),
    )
  }
  if (clientUnit === 'MW') {
    return data.map(item => convert(item, 0.001, false))
  }
  if (clientUnit === 'MWh') {
    return data.map(item =>
      convert(item, 0.001, true, ['Availability', 'Capacity']),
    )
  }
  return data
}

const creatSBLineSeries = (root, sbxAxis, sbyAxis, valueYField, scrollbarX) => {
  const sbseries = scrollbarX.chart.series.push(
    am5xy.LineSeries.new(root, {
      xAxis: sbxAxis,
      yAxis: sbyAxis,
      connect: false,
      valueYField: valueYField,
      valueXField: TIMESTAMP,
    }),
  )
  sbseries.fills.template.setAll({
    visible: true,
    fillOpacity: 0.3,
  })

  return sbseries
}

const createSBSeriesFromData = (root, scrollbarX, data) => {
  const sbxAxis = scrollbarX.chart.xAxes.push(
    am5xy.DateAxis.new(root, {
      baseInterval: { timeUnit: 'hour', count: 1 },
      renderer: am5xy.AxisRendererX.new(root, {
        opposite: false,
        strokeOpacity: 0,
      }),
    }),
  )

  const sbyAxis = scrollbarX.chart.yAxes.push(
    am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, {}),
    }),
  )

  const sbseries = []
  if (data.length !== 0) {
    const seriesNames = Object.keys(data[0])
    seriesNames.forEach(name => {
      if (name !== TIMESTAMP) {
        const sbLineSeries = creatSBLineSeries(
          root,
          sbxAxis,
          sbyAxis,
          name,
          scrollbarX,
        )
        sbLineSeries.data.setAll(data)
        sbseries.push(sbLineSeries)
      }
    })
  } else {
    console.error('Metrics data is empty.')
  }

  return sbseries
}

const createLineSeries = (
  root,
  xAxis,
  yAxis,
  seriesName,
  valueYField,
  unit,
) => {
  const formatNumberUnit = unit === 'MWh' || unit === 'MW' ? '#.#' : '#.'

  // Strip _S1/_S2 to get the base series name for color lookup
  const suffix = seriesName.endsWith('_S2')
    ? '_S2'
    : seriesName.endsWith('_S1')
      ? '_S1'
      : ''
  const baseName = suffix ? seriesName.slice(0, -3) : seriesName
  const isS2 = suffix === '_S2'

  const displayName = compareLegendLabels[seriesName] || seriesName

  const initObj = {
    name: displayName,
    xAxis: xAxis,
    yAxis: yAxis,
    locationX: 0,
    tooltipLocationX: 0,
    connect: false,
    valueYField: valueYField,
    valueXField: TIMESTAMP,
    tooltip: am5.Tooltip.new(root, {
      pointerOrientation: 'horizontal',
      labelText: "[bold]{valueY.formatNumber('" + formatNumberUnit + "')}",
    }),
  }

  let lineSeries = null
  if (baseName === 'Online') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0x33ff00),
      stroke: am5.color(0x33ff00),
    })
  } else if (baseName === 'Metering') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0x0000ff),
      stroke: am5.color(0x0000ff),
    })
  } else if (baseName === 'Forecast_50') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0xcc0000),
      stroke: am5.color(0xcc0000),
    })
  } else if (baseName === 'Availability') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0xff0000),
      stroke: am5.color(0xff0000),
    })
  } else if (baseName === 'Curtailment') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0xffa500),
      stroke: am5.color(0xffa500),
    })
  } else if (baseName === 'Downregulation') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
    })
    lineSeries.fills.template.setAll({
      fillOpacity: 0.3,
      visible: true,
    })
  } else if (baseName === 'Ecmwf_50') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0x01befe),
      stroke: am5.color(0x01befe),
    })
  } else if (baseName === 'Etaec_50') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0xffdd00),
      stroke: am5.color(0xffdd00),
    })
  } else if (baseName === 'Gfs_eta_50') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0xff7d00),
      stroke: am5.color(0xff7d00),
    })
  } else if (baseName === 'Icon_50') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0x8f00ff),
      stroke: am5.color(0x8f00ff),
    })
  } else if (baseName === 'Ukmo_50') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0xff006d),
      stroke: am5.color(0xff006d),
    })
  } else if (baseName === 'Ukmo100_50') {
    lineSeries = am5xy.LineSeries.new(root, {
      ...initObj,
      fill: am5.color(0xadff02),
      stroke: am5.color(0xadff02),
    })
  } else {
    lineSeries = am5xy.LineSeries.new(root, initObj)
  }
  lineSeries.strokes.template.set('strokeWidth', 1.5)

  // In compare mode, S2 series get dashed lines to visually distinguish
  if (isS2) {
    lineSeries.strokes.template.setAll({
      strokeDasharray: [6, 3],
      strokeWidth: 1.5,
    })
  }

  return lineSeries
}

const createSeriesFromData = (root, xAxis, yAxis, data, unit, resolution) => {
  const series = []

  if (data.length !== 0) {
    const seriesNames = Object.keys(data[0])
    seriesNames.forEach(name => {
      if (name !== TIMESTAMP) {
        // Strip _S1/_S2 to get base name for skip check
        const baseName =
          name.endsWith('_S1') || name.endsWith('_S2')
            ? name.slice(0, -3)
            : name

        // Skip right-side Y-axis series entirely
        if (SKIP_SERIES.has(baseName)) return

        const convertedData = unitsConversion(unit, resolution, data)

        const lineSeries = createLineSeries(
          root,
          xAxis,
          yAxis,
          name,
          name,
          unit,
        )

        lineSeries.data.setAll(convertedData)
        series.push(lineSeries)
      }
    })
  } else {
    console.error('Metrics data is empty.')
  }
  return series
}

const addDailyTimeLines = (timezone, xAxis, startMs, endMs, issueTime) => {
  const hour = Math.floor(issueTime / 60)
  const minute = issueTime % 60

  let day = moment.tz(startMs, timezone).startOf('day')
  const endDay = moment.tz(endMs, timezone).startOf('day')

  while (day.isSameOrBefore(endDay)) {
    const ts = day
      .clone()
      .hour(hour)
      .minute(minute)
      .second(0)
      .millisecond(0)
      .valueOf()

    const range = xAxis.createAxisRange(xAxis.makeDataItem({ value: ts }))
    range.get('grid').setAll({ strokeOpacity: 0.35, strokeWidth: 0.5 })
    range.get('label').set('forceHidden', true)

    day.add(1, 'day')
  }
}

const PerformanceCompareChart = props => {
  const classes = useStyles(props)
  const {
    chartDataRaw,
    chartName,
    selectedSetup,
    comparisonSelection,
    setTrackDisableStatus,
    resolution,
    issueTime,
  } = props
  const [chartIsReady, setChartIsReady] = useState(false)
  const containerRef = useRef(null)

  const timezone =
    loadStateFromLocalStorage('timezone') !== undefined
      ? loadStateFromLocalStorage('timezone')
      : 'Etc/UTC'

  const unit =
    loadStateFromLocalStorage('unit') !== undefined
      ? loadStateFromLocalStorage('unit')
      : 'kW'

  const chartData = chartDataRaw

  const {
    setupS1,
    setupS2,
    plantS1,
    plantS2,
    aggregationS1,
    aggregationS2,
    forecastType,
    issueTime: selectedIssueTime,
    leadTime,
  } = comparisonSelection || {}

  const targetS1 = plantS1 || aggregationS1
  const targetS2 = plantS2 || aggregationS2
  const commonTarget = targetS1 && targetS1 === targetS2 ? targetS1 : null

  const selectionTitleS1 = [
    setupS1 && `S1: ${setupS1}`,
    plantS1 && `Plant: ${plantS1}`,
    !plantS1 && aggregationS1 && `Aggregation: ${aggregationS1}`,
  ]
    .filter(Boolean)
    .join(' | ')

  const selectionTitleS2 = [
    setupS2 && `S2: ${setupS2}`,
    plantS2 && `Plant: ${plantS2}`,
    !plantS2 && aggregationS2 && `Aggregation: ${aggregationS2}`,
  ]
    .filter(Boolean)
    .join(' | ')

  const detailsTitle = [
    forecastType && `Horizon: ${forecastType}`,
    ['ID', 'DAH'].includes(forecastType) &&
      Number.isFinite(selectedIssueTime) &&
      `Issue time: ${formatMinutesAsTime(selectedIssueTime)} (${timezone})`,
    forecastType === 'Composite' &&
      Number.isFinite(leadTime) &&
      `Lead time: ${leadTime} min`,
  ]
    .filter(Boolean)
    .join(' | ')

  const chartTitleSections = [
    selectionTitleS1,
    selectionTitleS2,
    detailsTitle,
  ].filter(Boolean)

  const comparisonFileParts = commonTarget
    ? [setupS1 || selectedSetup, setupS2 && 'vs', setupS2, commonTarget]
    : [setupS1 || selectedSetup, targetS1, setupS2 && 'vs', setupS2, targetS2]

  const exportFilePrefix = [
    ...comparisonFileParts,
    forecastType,
    unit,
    'compare_timeseries',
  ]
    .filter(Boolean)
    .join('_')

  useEffect(() => {
    const initializeChart = () => {
      try {
        if (typeof am5.registry.rootElements !== 'undefined') {
          am5.array.each(am5.registry.rootElements, function (root) {
            if (root && root.dom && root.dom.id === chartName) {
              root.dispose()
            }
          })
        }
        const root = am5.Root.new(chartName)
        root._logo.dispose()
        root.timezone = am5.Timezone.new(timezone)

        const chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            panX: false,
            wheelY: 'zoomX',
            layout: root.verticalLayout,
          }),
        )

        if (chartTitleSections.length) {
          const titleContainer = chart.topAxesContainer.children.unshift(
            am5.Container.new(root, {
              width: am5.percent(100),
              layout: root.horizontalLayout,
            }),
          )

          chartTitleSections.forEach((text, index) => {
            const isFirst = index === 0
            const isLast = index === chartTitleSections.length - 1

            titleContainer.children.push(
              am5.Label.new(root, {
                text,
                width: am5.percent(100 / chartTitleSections.length),
                fontSize: 14,
                fontWeight: '300',
                textAlign: isFirst ? 'left' : isLast ? 'right' : 'center',
                oversizedBehavior: 'wrap',
                breakWords: false,
              }),
            )
          })
        }

        // Create power Y-axis
        const yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            marginRight: 20,
            extraTooltipPrecision: unit === 'MWh' || unit === 'MW' ? 1 : 0,
            renderer: am5xy.AxisRendererY.new(root, {}),
          }),
        )
        const powerLabel = am5.Label.new(root, {
          rotation: -90,
          text:
            unit === 'kW' || unit === 'MW'
              ? 'Power (' + unit + ')'
              : 'Production (' + unit + ')',
          y: am5.p50,
          centerX: am5.p50,
        })
        yAxis.children.unshift(powerLabel)

        // Create time X-Axis
        const xAxis = chart.xAxes.push(
          am5xy.DateAxis.new(root, {
            groupData: false,
            marginTop: 20,
            tooltipDateFormat: 'yyyy-MM-dd HH:mm',
            maxDeviation: 0.1,
            baseInterval: { timeUnit: 'minute', count: resolution },
            renderer: am5xy.AxisRendererX.new(root, {}),
          }),
        )
        const timeLabel = am5.Label.new(root, {
          text: timezone,
          x: am5.percent(50),
        })
        xAxis.children.push(timeLabel)
        xAxis.get('renderer').grid.template.setAll({
          strokeOpacity: 0,
        })
        xAxis.get('dateFormats')['month'] = 'yyyy-MM-dd'
        xAxis.get('dateFormats')['day'] = 'yyyy-MM-dd\nHH:mm'
        xAxis.get('dateFormats')['hour'] = 'yyyy-MM-dd\nHH:mm'
        xAxis.get('dateFormats')['minute'] = 'yyyy-MM-dd\nHH:mm'

        /** Add this to minimize tooltip distance from cursor axis */
        const r = xAxis.get('renderer')
        xAxis.set('tooltipLocation', 0)
        r.labels.template.setAll({ location: 0 })
        r.grid.template.setAll({ location: 0 })

        /** Add vertical lines for issue time */
        const startDate = chartData[0]?.Timestamp
        const endDate = chartData.at(-1)?.Timestamp

        if (startDate && endDate) {
          addDailyTimeLines(timezone, xAxis, startDate, endDate, issueTime)
        }

        const series = createSeriesFromData(
          root,
          xAxis,
          yAxis,
          chartData,
          unit,
          resolution,
        )
        series.forEach(lineSeries => {
          chart.series.push(lineSeries)
        })

        // Add scrollbarX with series data
        const scrollbarX = am5xy.XYChartScrollbar.new(root, {
          orientation: 'horizontal',
          height: 75,
        })
        chart.set('scrollbarX', scrollbarX)
        createSBSeriesFromData(root, scrollbarX, chartData)

        // Add cursor
        chart.set(
          'cursor',
          am5xy.XYCursor.new(root, {
            behavior: 'zoomXY',
            xAxis: xAxis,
            yAxis: yAxis,
          }),
        )

        xAxis.set(
          'tooltip',
          am5.Tooltip.new(root, {
            themeTags: ['axis'],
          }),
        )

        yAxis.set(
          'tooltip',
          am5.Tooltip.new(root, {
            themeTags: ['axis'],
          }),
        )

        // add legend
        const legend = chart.children.push(
          am5.Legend.new(root, {
            layout: root.gridLayout,
            centerX: am5.percent(50),
            x: am5.percent(50),
          }),
        )

        // hide all series at the start except Forecast P50 and Online
        chart.series.each(series => {
          if (!SHOW_ON_START.has(series.get('name'))) {
            series.set('visible', false)
            series.hide(0)
          }
        })

        legend.data.setAll(chart.series.values)

        let menu = am5plugins_exporting.ExportingMenu.new(root, {})

        let exporting = am5plugins_exporting.Exporting.new(root, {
          menu: menu,
          pdfOptions: { disabled: true },
          pdfdataOptions: { disabled: true },
          jsonOptions: { disabled: true },
          htmlOptions: { disabled: true },
          dateFormat: 'yyyy-MM-dd HH:mm:ss',
          dataSource: unitsConversion(unit, resolution, chartData),
          filePrefix: exportFilePrefix,
          csvOptions: {
            separator: ';',
            addColumnNames: true,
            addBOM: true,
          },
        })

        exporting.events.on('dataprocessed', function (ev) {
          ev.data = ev.data.map(function (row) {
            var Timestamp = row.Timestamp
            var rest = Object.assign({}, row)
            delete rest.Timestamp
            Object.keys(rest).forEach(function (key) {
              if (rest[key] === null || rest[key] === undefined) {
                rest[key] = ''
              }
            })
            return Object.assign(
              {
                [`Timestamp(${timezone})`]: moment
                  .utc(Timestamp)
                  .tz(timezone)
                  .format('YYYY-MM-DD HH:mm:ss'),
              },
              rest,
            )
          })
        })

        // catch the event when chart is ready
        let timeout
        root.events.on('frameended', exportChart)
        function exportChart() {
          if (timeout) {
            clearTimeout(timeout)
          }
          timeout = setTimeout(function () {
            root.events.off('frameended', exportChart)
            setChartIsReady(true)
            setTrackDisableStatus(false)
          }, 100)
        }
      } catch (error) {
        console.error('Error in PerformanceCompare TimeSeries chart:', error)
      }
    }

    if (chartData && containerRef.current && !chartIsReady) {
      setTimeout(() => {
        initializeChart()
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, chartIsReady])

  return (
    <>
      {!chartIsReady && (
        <Box sx={{ width: '100%', marginTop: '400px' }}>
          <Typography
            variant="h4"
            className={clsx(classes.formSubtitle, classes.customSubtitle)}
          >
            Generating graph
          </Typography>
          <LinearProgress />
        </Box>
      )}
      <div
        ref={containerRef}
        id={chartName}
        style={{
          width: '100%',
          height: '750px',
          opacity: chartIsReady ? '1' : '0',
        }}
      ></div>
    </>
  )
}

export default PerformanceCompareChart
